import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { debounce } from "lodash";
import { useReactToPrint } from "react-to-print";

import { AllderTable } from "../../../../components/AllderTable";
import { memberArInvoiceHandler } from "../../../../redux/handlers";
import ModalAllder from "components/layout/ModalAllder";
import { PrintRE } from "../print/PrintRE";

function fmt(value) {
  if (value == null) return "0.00";
  return new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

function fmtDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("th-TH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export default function ReceiptTab() {
  const { businessSlug, customerID } = useParams();
  const dispatch = useDispatch();

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState("");

  // print
  const printRef = useRef();
  const [printData, setPrintData] = useState({ receipt: null, invoice: null });
  const handlePrint = useReactToPrint({ contentRef: printRef });

  // delete confirm modal
  const [deleteModal, setDeleteModal] = useState({
    mode: "normal",
    open: false,
    width: 480,
    height: 320,
    x: 0,
    y: 0,
    modalname: "delete-receipt-modal",
    pin: false,
    row: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const debouncedSetFilter = useMemo(
    () => debounce((value) => setFilterText(value), 600),
    [],
  );

  useEffect(() => () => debouncedSetFilter.cancel(), [debouncedSetFilter]);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const handleExport = useCallback(() => {
    dispatch(
      memberArInvoiceHandler.getMemberArReceiptExcel(businessSlug, {
        member_id: customerID,
        search: filterText || undefined,
      }),
    );
  }, [dispatch, businessSlug, customerID, filterText]);

  const fetchReceipts = useCallback(async () => {
    if (!businessSlug || !customerID) return;
    try {
      setLoading(true);
      const res = await dispatch(
        memberArInvoiceHandler.getMemberArReceiptPagination(businessSlug, {
          member_id: customerID,
          page: paginationModel.page + 1,
          per_page: paginationModel.pageSize,
          search: filterText || undefined,
        }),
      );
      if (res) {
        setReceipts(res.data ?? []);
        setRowCount(res.meta?.total ?? res.total ?? 0);
      }
    } catch (err) {
      console.error("Error fetching receipts:", err);
    } finally {
      setLoading(false);
    }
  }, [
    dispatch,
    businessSlug,
    customerID,
    paginationModel.page,
    paginationModel.pageSize,
    filterText,
  ]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handlePrintRow = useCallback(
    async (row) => {
      const invoiceId = row.memberArInvoice?.id;
      if (!invoiceId) return;
      try {
        const fullInvoice = await dispatch(
          memberArInvoiceHandler.getMemberArInvoiceById(invoiceId),
        );
        setPrintData({
          receipt: row,
          invoice: fullInvoice ?? row.memberArInvoice,
        });
        setTimeout(() => handlePrint(), 100);
      } catch (err) {
        console.error("Error fetching invoice for print:", err);
      }
    },
    [dispatch, handlePrint],
  );

  const handleCancelReceiptConfirm = useCallback(async () => {
    const row = deleteModal.row;
    if (!row) return;
    // row คือ memberArPaymentReceiptInvoice record
    // invoiceId = row.memberArInvoice?.id (member_ar_invoice_id)
    // receiptInvoiceId = row.id (junction record id ที่ backend ใช้ lookup)
    const invoiceId = row.memberArInvoice?.id;
    const receiptInvoiceId = row.id;
    if (!invoiceId || !receiptInvoiceId) return;
    setIsDeleting(true);
    try {
      const res = await dispatch(
        memberArInvoiceHandler.cancelReceipt(invoiceId, receiptInvoiceId),
      );
      if (res !== undefined) {
        setDeleteModal((prev) => ({ ...prev, open: false, row: null }));
        fetchReceipts();
      }
    } catch (err) {
      console.error("Error cancelling receipt:", err);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.row, dispatch, fetchReceipts]);

  const columns = [
    {
      field: "document_no",
      headerName: "เลขที่ใบเสร็จ",
      width: 190,
      sortable: false,
      renderCell: ({ row }) => {
        const docNo = row.memberArPaymentReceipt?.document_no;
        return <div>{docNo}</div>;
      },
    },
    {
      field: "completed_at",
      headerName: "วันที่รับชำระ",
      width: 130,
      sortable: false,
      renderCell: ({ row }) =>
        fmtDate(row.completed_at ?? row.memberArPaymentReceipt?.created_at),
    },
    {
      field: "invoice_document_no",
      headerName: "เลขที่ใบแจ้งหนี้",
      width: 175,
      sortable: false,
      renderCell: ({ row }) => {
        const inv = row.memberArInvoice;
        if (!inv) return "-";
        return (
          <Link
            to={`/${businessSlug}/report/debtor/manage/${inv.id}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#565DFF", textDecoration: "underline" }}
          >
            {inv.document_no}
          </Link>
        );
      },
    },
    {
      field: "lines_subtotal",
      headerName: "ยอดรวม",
      width: 120,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.lines_subtotal),
    },
    {
      field: "receipt_discount_amount",
      headerName: "ส่วนลด",
      width: 110,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.receipt_discount_amount),
    },
    {
      field: "withholding_tax_amount",
      headerName: "หัก ณ ที่จ่าย",
      width: 120,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => fmt(row.withholding_tax_amount),
    },
    {
      field: "net_paid_amount",
      headerName: "ยอดสุทธิที่รับ",
      width: 130,
      sortable: false,
      align: "right",
      headerAlign: "right",
      renderCell: ({ row }) => (
        <span className="font-semibold text-[#2DC76D]">
          {fmt(row.net_paid_amount ?? row.grand_total)}
        </span>
      ),
    },
    {
      field: "payment_methods",
      headerName: "วิธีชำระเงิน",
      width: 160,
      sortable: false,
      renderCell: ({ row }) => {
        const splits = row.splits ?? [];
        if (splits.length === 0) return "-";
        return splits.map((s) => s.paymentMethod?.name ?? "-").join(", ");
      },
    },
    {
      field: "createdByUserBusinessGroup",
      headerName: "ผู้รับชำระ",
      width: 300,
      sortable: false,
      renderCell: ({ row }) => {
        const ubg = row.createdByUserBusinessGroup;
        if (!ubg) return "-";
        return `${ubg.first_name || ""} ${ubg.last_name || ""}`.trim() || "-";
      },
    },
    {
      field: "actions",
      headerName: "จัดการ",
      width: 110,
      sortable: false,
      renderCell: ({ row }) => (
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-print-st-1"
            title="พิมพ์ใบเสร็จ"
            onClick={() => handlePrintRow(row)}
          />
          <button
            type="button"
            className="btn btn-close-st-2"
            title="ลบใบเสร็จ"
            onClick={() =>
              setDeleteModal((prev) => ({ ...prev, open: true, row }))
            }
          />
        </div>
      ),
    },
  ];

  return (
    <div className="card card-st-2">
      {/* hidden print area */}
      <div style={{ display: "none" }}>
        <PrintRE
          innerRef={printRef}
          receipt={printData.receipt}
          invoice={printData.invoice}
        />
      </div>

      {/* confirm cancel receipt modal */}
      <ModalAllder
        modal={deleteModal}
        setModal={setDeleteModal}
        htitle="ยืนยันการยกเลิกใบเสร็จ"
        hbtn={false}
      >
        <div className="text-center py-4">
          <WarningAmberIcon sx={{ fontSize: 80, color: "#FF6B00" }} />
          <div
            className="mt-3 text-lg font-semibold"
            style={{ color: "#2F4858" }}
          >
            ยืนยันการยกเลิกใบเสร็จ
          </div>
          <div
            className="mt-2 text-sm"
            style={{ color: "rgba(76,78,100,0.68)" }}
          >
            เลขที่ใบเสร็จ:{" "}
            <span className="font-semibold">
              {deleteModal.row?.memberArPaymentReceipt?.document_no ?? "-"}
            </span>
          </div>
          <div
            className="mt-1 text-sm"
            style={{ color: "rgba(76,78,100,0.68)" }}
          >
            คุณต้องการยกเลิกใบเสร็จนี้หรือไม่?
          </div>
        </div>
        <div className="flex justify-center gap-3 pt-2 pb-4">
          <Button
            variant="outlined"
            color="gray"
            disabled={isDeleting}
            onClick={() =>
              setDeleteModal((prev) => ({ ...prev, open: false, row: null }))
            }
          >
            ปิด
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={isDeleting}
            onClick={handleCancelReceiptConfirm}
          >
            {isDeleting ? "กำลังดำเนินการ..." : "ยืนยันการยกเลิก"}
          </Button>
        </div>
      </ModalAllder>

      <div className="card-body px-0 pt-4 pb-0">
        <div className="d-flex justify-content-between align-items-center px-4 mb-3 flex-wrap gap-2">
          <div>
            <button className="btn btn-export-st-1" onClick={handleExport}>
              Export
            </button>
          </div>
          <div className="mx-4">
            <input
              type="text"
              className="form-control form-st-4"
              placeholder="ค้นหาเลขที่ใบเสร็จ / ใบแจ้งหนี้"
              onChange={(e) => debouncedSetFilter(e.target.value)}
            />
          </div>
        </div>

        <AllderTable
          rows={receipts}
          columns={columns}
          loading={loading}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[20, 50, 100]}
        />
      </div>
    </div>
  );
}
